import { Property } from 'csstype'
import { AsH1, TitleLG } from '@/atoms/Text'
import { Translate, TranslateFunction, useTranslate } from '@/utils/translate/translate-client'
import { useWatchTheatricalContext } from '../WatchTheatricalContext'
import { ChooseCtaInput, chooseCtas, CtaCode } from './rules'

type CtaStatusLineProps = {
  friendlyTheatricalReleaseDate: ReturnType<typeof useWatchTheatricalContext>['friendlyTheatricalReleaseDate']
  /** Raw release date (ISO string or Date) for past/future comparison */
  theatricalReleaseDate?: string | Date | null
  heroCta?: ReturnType<typeof useWatchTheatricalContext>['heroCta']
  isInTheaters: boolean
  projectName: string
} & ChooseCtaInput
export function CtaStatusLine(props: CtaStatusLineProps) {
  const { t } = useTranslate('theatrical-presales')

  const declaration = mapDeclaration(t, props, chooseCtas(props))

  if (!declaration) return null

  return (
    <TitleLG id="cta-status-line" as={AsH1} className="min-h-[26px] px-2 text-center" color="white" weight="bold">
      <span className="sr-only">{props.projectName} </span>
      {declaration}
    </TitleLG>
  )
}

export function mapDeclaration(t: TranslateFunction, props: CtaStatusLineProps, codes: CtaCode[]) {
  if (props.heroCta?.description) return props.heroCta.description
  if (codes.includes('stream')) return t('nowStreaming', 'Now streaming')
  if (codes.includes('joinGuild') && props.isStreaming)
    return t('nowStreamingForGuildMembers', 'Now Streaming for Guild Members')
  if (codes.includes('theater') && props.friendlyTheatricalReleaseDate) {
    return <InTheatersDeclaration {...props} />
  }
}

function InTheatersDeclaration(props: CtaStatusLineProps) {
  const { t } = useTranslate('theatrical-presales')
  const rawDate = props.theatricalReleaseDate ?? props.filmDetails?.theatricalReleaseDate
  const releaseDate = rawDate ? new Date(rawDate) : null
  const isPastReleaseDate = releaseDate && !isNaN(releaseDate.getTime()) && new Date() > releaseDate
  return isPastReleaseDate ? (
    <Translate
      i18nKey="inTheatersNow"
      t={t}
      values={{ friendlyTheatricalRelease: props.friendlyTheatricalReleaseDate }}
    >
      IN THEATERS{' '}
      <span className="uppercase" style={{ color: props.filmDetails?.brandColor as Property.Color }}>
        NOW
      </span>
    </Translate>
  ) : (
    <Translate
      i18nKey="inTheatersOnDate"
      t={t}
      values={{ friendlyTheatricalRelease: props.friendlyTheatricalReleaseDate }}
    >
      IN THEATERS{' '}
      <span className="uppercase" style={{ color: props.filmDetails?.brandColor as Property.Color }}>
        {{ friendlyTheatricalReleaseDate: props.friendlyTheatricalReleaseDate }}
      </span>
    </Translate>
  )
}
